import { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, Gift, User, MessageSquare, TrendingUp, 
  Users, Settings, ChevronRight, Menu, X, Trophy, Share2, LogOut,
  Bell, Rocket, PlusCircle, Briefcase, Home, Link2, Sparkles, MapPin,
  Shield, FileText, DollarSign, ClipboardCheck, Lock, BarChart3, 
  AlertTriangle, Calendar, Hammer, CheckCircle, Activity, ShoppingBag
} from 'lucide-react';
import { Button } from '../components/ui/button';

const Dashboard = () => {
  const { user, logout, isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, loading, navigate]);

  // Check if user needs approval (sponsors/contractors)
  const needsApproval = (user?.role === 'sponsor' || user?.role === 'contractor') 
    && user?.approval_status !== 'approved';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="w-12 h-12 border-4 border-[#FF3B30]/20 border-t-[#FF3B30] rounded-full animate-spin" />
      </div>
    );
  }

  const isHomeowner = user?.role === 'homeowner';
  const isSponsor = user?.role === 'sponsor';
  const isContractor = user?.role === 'contractor';
  const isAdmin = user?.role === 'admin';
  const isSuperAdmin = user?.role === 'super_admin';

  // Homeowner Navigation - Entry status, profile, nominations, giveaways
  const homeownerNav = [
    { name: 'Overview', href: '/dashboard', icon: LayoutDashboard },
    { name: 'My Entries', href: '/dashboard/entries', icon: Gift },
    { name: 'Available Giveaways', href: '/giveaways', icon: Trophy },
    { name: 'Nominate Neighbor', href: '/dashboard/nominate', icon: Users },
    { name: 'Property Estimate', href: '/dashboard/property-estimate', icon: Home },
    { name: 'Notifications', href: '/dashboard/notifications', icon: Bell },
    { name: 'Profile', href: '/dashboard/profile', icon: User },
  ];

  // Sponsor/Realtor Navigation - Campaign performance, leads, payments, territory
  const sponsorNav = [
    { name: 'Overview', href: '/dashboard', icon: LayoutDashboard },
    { name: 'My Territory', href: '/dashboard/territory', icon: MapPin },
    { name: 'Create Giveaway', href: '/dashboard/create-giveaway', icon: PlusCircle },
    { name: 'My Leads', href: '/dashboard/leads', icon: Users },
    { name: 'Campaign Analytics', href: '/dashboard/analytics', icon: BarChart3 },
    { name: 'Payment Status', href: '/dashboard/payments', icon: DollarSign },
    { name: 'Affiliate Links', href: '/dashboard/affiliate', icon: Link2 },
    { name: 'Notifications', href: '/dashboard/notifications', icon: Bell },
    { name: 'Profile', href: '/dashboard/profile', icon: User },
  ];

  // Contractor Navigation - Projects, jobs, scheduling, payouts
  const contractorNav = [
    { name: 'Overview', href: '/dashboard', icon: LayoutDashboard },
    { name: 'My Projects', href: '/dashboard/projects', icon: Hammer },
    { name: 'Winner Jobs', href: '/dashboard/winner-jobs', icon: CheckCircle },
    { name: 'Schedule', href: '/dashboard/schedule', icon: Calendar },
    { name: 'Payout Status', href: '/dashboard/payouts', icon: DollarSign },
    { name: 'Portfolio', href: '/dashboard/contractor-portfolio', icon: Briefcase },
    { name: 'Notifications', href: '/dashboard/notifications', icon: Bell },
    { name: 'Profile', href: '/dashboard/profile', icon: User },
  ];

  // Admin Navigation - Manage users, sponsors, giveaways, approvals
  const adminNav = [
    { name: 'Overview', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Approvals', href: '/dashboard/admin/approvals', icon: ClipboardCheck },
    { name: 'Users', href: '/dashboard/admin/users', icon: Users },
    { name: 'Giveaways', href: '/dashboard/admin/giveaways', icon: Gift },
    { name: 'Entries', href: '/dashboard/admin/entries', icon: FileText },
    { name: 'Sponsors', href: '/dashboard/admin/sponsors', icon: Trophy },
    { name: 'Contractors', href: '/dashboard/admin/contractors', icon: Hammer },
    { name: 'Fraud Detection', href: '/dashboard/admin/fraud', icon: AlertTriangle },
    { name: 'Winner Selection', href: '/dashboard/admin/winners', icon: Sparkles },
    { name: 'Ops & Usage', href: '/dashboard/admin/ops', icon: Activity },
    { name: 'Referral Program', href: '/dashboard/admin/referrals', icon: Share2 },
    { name: 'Merch Orders', href: '/dashboard/admin/merch-orders', icon: ShoppingBag },
    { name: 'Territories', href: '/dashboard/territory', icon: MapPin },
    { name: 'Notifications', href: '/dashboard/notifications', icon: Bell },
  ];

  // Super Admin Navigation - Everything admin has + system settings, audit, compliance
  const superAdminNav = [
    { name: 'Overview', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Approvals', href: '/dashboard/admin/approvals', icon: ClipboardCheck },
    { name: 'Users', href: '/dashboard/admin/users', icon: Users },
    { name: 'Giveaways', href: '/dashboard/admin/giveaways', icon: Gift },
    { name: 'Entries', href: '/dashboard/admin/entries', icon: FileText },
    { name: 'Sponsors', href: '/dashboard/admin/sponsors', icon: Trophy },
    { name: 'Contractors', href: '/dashboard/admin/contractors', icon: Hammer },
    { name: 'Fraud Detection', href: '/dashboard/admin/fraud', icon: AlertTriangle },
    { name: 'Winner Selection', href: '/dashboard/admin/winners', icon: Sparkles },
    { name: 'Ops & Usage', href: '/dashboard/admin/ops', icon: Activity },
    { name: 'Referral Program', href: '/dashboard/admin/referrals', icon: Share2 },
    { name: 'Merch Orders', href: '/dashboard/admin/merch-orders', icon: ShoppingBag },
    { name: 'Territories', href: '/dashboard/territory', icon: MapPin },
    // Super Admin Only
    { name: 'Audit Logs', href: '/dashboard/admin/audit', icon: FileText, superOnly: true },
    { name: 'Role Management', href: '/dashboard/admin/roles', icon: Shield, superOnly: true },
    { name: 'Compliance', href: '/dashboard/admin/compliance', icon: Lock, superOnly: true },
    { name: 'System Settings', href: '/dashboard/admin/settings', icon: Settings, superOnly: true },
    { name: 'Founder Hub', href: '/dashboard/admin/founder', icon: Rocket },
  ];

  // Select navigation based on role
  const getNavigation = () => {
    if (isSuperAdmin) return superAdminNav;
    if (isAdmin) return adminNav;
    if (isContractor) return contractorNav;
    if (isSponsor) return sponsorNav;
    return homeownerNav;
  };

  const navigation = getNavigation();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Show pending approval screen for sponsors/contractors who aren't approved
  if (needsApproval) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <ClipboardCheck className="w-8 h-8 text-amber-600" />
          </div>
          <h1 className="text-2xl font-bold text-neutral-900 mb-2" style={{ fontFamily: 'Outfit' }}>
            {user?.approval_status === 'pending' ? 'Approval Pending' : 
             user?.approval_status === 'rejected' ? 'Application Rejected' :
             user?.approval_status === 'suspended' ? 'Account Suspended' : 'Awaiting Review'}
          </h1>
          <p className="text-neutral-600 mb-6">
            {user?.approval_status === 'pending' && 
              `Your ${user?.role} application is being reviewed by our team. You'll receive an email once approved.`}
            {user?.approval_status === 'rejected' && 
              'Your application was not approved. Please contact support for more information.'}
            {user?.approval_status === 'suspended' && 
              'Your account has been suspended. Please contact support to resolve this issue.'}
          </p>
          <div className="space-y-3">
            <Button 
              onClick={handleLogout}
              variant="outline" 
              className="w-full"
            >
              Log Out
            </Button>
            <a href="mailto:support@tryspinser.com" className="block text-sm text-[#FF3B30] hover:underline">
              Contact Support
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-neutral-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <Link to="/" className="text-xl font-bold" style={{ fontFamily: 'Outfit' }}>
            <span className="text-[#0A0A0A]">Spin</span>
            <span className="text-[#FF3B30]">ser</span>
          </Link>
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2">
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-neutral-200 transform transition-transform lg:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-neutral-200">
            <Link to="/" className="text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>
              <span className="text-[#0A0A0A]">Spin</span>
              <span className="text-[#FF3B30]">ser</span>
            </Link>
          </div>

          {/* User Info */}
          <div className="p-4 border-b border-neutral-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#FF3B30] flex items-center justify-center text-white font-medium">
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-neutral-900 truncate">{user?.name}</p>
                <p className="text-sm text-neutral-500 capitalize">{user?.role}</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                    isActive 
                      ? 'bg-[#FF3B30]/10 text-[#FF3B30]' 
                      : 'text-neutral-600 hover:bg-neutral-100'
                  }`}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.name}</span>
                  {item.superOnly && (
                    <span className="ml-auto px-1.5 py-0.5 text-[10px] font-semibold bg-purple-100 text-purple-700 rounded">
                      SA
                    </span>
                  )}
                  {isActive && !item.superOnly && <ChevronRight className="w-4 h-4 ml-auto" />}
                </Link>
              );
            })}
          </nav>

          {/* Bottom */}
          <div className="p-4 border-t border-neutral-200">
            <Link to="/giveaways">
              <Button className="w-full mb-3 bg-[#FF3B30] hover:bg-[#DC2626] text-white rounded-xl" data-testid="browse-giveaways-btn">
                Browse Giveaways
              </Button>
            </Link>
            <button 
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 py-2 text-neutral-600 hover:text-red-600 transition-colors"
              data-testid="sidebar-logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Log out
            </button>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:pl-64 pt-16 lg:pt-0 min-h-screen">
        <div className="p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
